import { Module } from "../module";
import { clamp, EnableDustParticlesMessage, ModuleId, randFloat } from "flavr-common";
import { InstanceBatch } from "../rendering/instancing/InstanceBatch";
import { DoubleSide, PlaneGeometry, RawShaderMaterial, StaticDrawUsage, Vector2 } from "three";
import { RenderOrder } from "../rendering/RenderOrder";
import { InstanceParticle } from "../rendering/instancing/particles/instanced.particle";
import { mainScene } from "../rendering/mainScene";
import { PARTICLE_INSTANCE_SHADER } from "../rendering/instancing/particles/particle.shader";
import { Vector3 } from "flavr-math";
import { animationFrame$ } from "../../messages/xr/AnimationFrame";
import { now } from "../../messages/timing";
const tmpVector3 = new Vector3();
const tmpVector2 = new Vector2();
const NUM_PARTICLES = 1024 * 8;
/**
 * Manages particle effects that are driven through bus messages rather than being hard-coded into the renderer.
 *
 * Messages:
 * - `EnableDustParticlesMessage` - Toggles dust particle behaviour at runtime.
 */
export class ParticleModule extends Module {
    /**
     * Subscribes to `EnableDustParticlesMessage` to show/hide or fade the dust particle batch, and registers the
     * per-frame update that advances any running fade.
     */
    constructor() {
        super(ModuleId.PARTICLES);
        this.connection.subscribe(EnableDustParticlesMessage.typestatic, (msg) => {
            if (EnableDustParticlesMessage.is(msg)) {
                const batch = this.getDustParticleBatch();
                if (msg.fadeDuration) {
                    if (this.fadeDustParticles && this.fadeDustParticles.fadeIn === msg.enable) {
                        return;
                    }
                    batch.setVisible(true);
                    this.fadeDustParticles = {
                        fadeIn: msg.enable,
                        startTime: now(),
                        duration: msg.fadeDuration
                    };
                }
                else {
                    batch.setVisible(msg.enable);
                    this.getDustParticelMaterial().uniforms.alpha.value = 1.0;
                    this.fadeDustParticles = undefined;
                }
                this.getDustParticelMaterial().uniforms.minY.value = msg.minY;
            }
        });
        animationFrame$.onPreRender$.add(() => {
            this.update();
        });
    }
    /**
     * Pre-creates all dust particle instances at random positions and offsets, updates the instance batch once, then
     * hides it until it is explicitly enabled.
     */
    init() {
        const batch = this.getDustParticleBatch();
        for (let i = 0; i < NUM_PARTICLES; ++i) {
            tmpVector3.set(randFloat(-1, 1), randFloat(-1, 1), randFloat(-1, 1));
            tmpVector2.set(randFloat(-1, 1), randFloat(-1, 1)).normalize().multiplyScalar(0.0005);
            const particle = batch.createInstance();
            particle.setPosition(tmpVector3);
            particle.setOffset(randFloat(0, 1), randFloat(0, 1), tmpVector2.x, tmpVector2.y);
        }
        batch.update();
        batch.setVisible(false);
    }
    getDustParticelMaterial() {
        if (!this.dustParticleMaterial) {
            const materialOptions = {
                defines: {
                    USE_LIGHTS: "",
                    USE_SHADOWMAP: ""
                },
                uniforms: PARTICLE_INSTANCE_SHADER.uniforms,
                vertexShader: PARTICLE_INSTANCE_SHADER.vertexShader,
                fragmentShader: PARTICLE_INSTANCE_SHADER.fragmentShader,
                lights: true,
                side: DoubleSide,
                wireframe: false,
                transparent: true,
                alphaTest: 0.4
            };
            this.dustParticleMaterial = new RawShaderMaterial(materialOptions);
        }
        return this.dustParticleMaterial;
    }
    getDustParticleBatch() {
        if (!this.dustParticleBatch) {
            this.dustParticleBatch = new InstanceBatch(mainScene, new PlaneGeometry(1, 1), this.getDustParticelMaterial(), PARTICLE_INSTANCE_SHADER.attributes, RenderOrder.MOVE_DATA_SELECTION_FILTER, InstanceParticle, NUM_PARTICLES, StaticDrawUsage, false, false);
        }
        return this.dustParticleBatch;
    }
    /**
     * If a fade is in progress, updates the dust particle material's alpha based on elapsed time and, once the fade
     * completes, sets the batch's final visibility and clears the fade state.
     */
    update() {
        if (this.fadeDustParticles) {
            const progress = (now() - this.fadeDustParticles.startTime) / this.fadeDustParticles.duration;
            this.getDustParticelMaterial().uniforms.alpha.value = clamp(this.fadeDustParticles.fadeIn ? progress : 1 - progress, 0, 1);
            if (progress >= 1) {
                this.getDustParticleBatch().setVisible(this.fadeDustParticles.fadeIn);
                this.fadeDustParticles = undefined;
            }
        }
    }
}
